import { useTranslation, Trans } from 'react-i18next';
import { useRouter } from 'next/router';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { setLoad } from '@/redux/formsReducer';
import { useEffect, useState } from 'react';
import { setError } from '@/redux/rootReducer';

import { formRestoreEmail, formRestorePassword } from '@/helper/fields';
import {
  restoreFormByEmail,
  restoreFormPassword,
} from '@/helper/shemaValidation/restorePassword';
import { post } from '@/helper/axiosRequest';
import { IForgotPasswordData, ISetPassword } from '@/types/apiTypes';

import FormRestorePassword from '@/components/formRestorePassword';
import HeadData from '@/components/headData';
import Header from '@/components/header';
import ErrorForm from '@/components/UI/errorForm';
import Button from '@/components/UI/button';
import CustomLink from '@/components/UI/link';

import style from './restorePassword.module.scss';

const RestorePassword = () => {
  const { i18n, t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const error = useAppSelector((state) => state.rootState.error);

  const [isSuccessEmail, setIsSuccessEmail] = useState(false);
  const [isSuccessPassword, setIsSuccessPassword] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [code, setCode] = useState(router.query.code);

  useEffect(() => {
    if (router.query.code) {
      setCode(router.query.code);
    }
  }, [router.query.code]);

  const requestForgotPassword = (values: IForgotPasswordData) => {
    dispatch(setLoad(true));
    post<any>('forgetPassword', { ...values }, i18n.language)
      .then(() => {
        setIsSuccessEmail(true);
        setClientEmail(values.email);
        dispatch(setLoad(false));
      })
      .catch((error) => {
        dispatch(setError(true));
        dispatch(setLoad(false));
      });
  };

  const requestNewPassword = (values: ISetPassword) => {
    dispatch(setLoad(true));
    post<any>('setPassword', { ...values }, i18n.language)
      .then(() => {
        setIsSuccessPassword(true);
        dispatch(setLoad(false));
      })
      .catch((error) => {
        dispatch(setError(true));
        dispatch(setLoad(false));
      });
  };

  const renderFormRestorePasswordByEmail = () => (
    <FormRestorePassword
      initialValue={{ email: '' }}
      validationSchema={restoreFormByEmail}
      fields={formRestoreEmail}
      textButton={t('formForgotPassword.emailButton')}
      submitFunction={requestForgotPassword}
      link={true}
    />
  );

  const renderRestorePassword = (code: string) => (
    <FormRestorePassword
      initialValue={{ password: '', confirmPassword: '' }}
      validationSchema={restoreFormPassword}
      fields={formRestorePassword}
      textButton='Пітвердити'
      submitFunction={requestNewPassword}
      code={code}
    />
  );

  const renderForm = () =>
    code
      ? renderRestorePassword(code as string)
      : renderFormRestorePasswordByEmail();

  const renderSuccessEmail = () =>
    isSuccessEmail && (
      <div className={style.email}>
        <p className={style.email_text}>
          {t('formForgotPassword.topSuccess', { clientEmail })}
        </p>
        <p className={style.email_text}>
          {t('formForgotPassword.bottomSuccess')}
        </p>
      </div>
    );

  const renderSuccessPassword = () =>
    isSuccessPassword && (
      <div className={style.wrapper_success_password}>
        <p className={style.success_password__describe}>
          {t('formForgotPassword.successPassword')}
        </p>
        <CustomLink
          href='/'
          target={false}
          style={style.success_password_button}
          text='formForgotPassword.successPasswordButton'
        />
      </div>
    );

  const renderContent = () => {
    if (error) {
      return (
        <>
          <ErrorForm />
          <Button
            text='formForgotPassword.reload'
            func={() => dispatch(setError(false))}
          />
        </>
      );
    } else {
      return (
        <>
          {!isSuccessEmail && !isSuccessPassword && renderForm()}
          {renderSuccessEmail()}
          {renderSuccessPassword()}
        </>
      );
    }
  };

  return (
    <section>
      <HeadData title='Restore Password' />
      <Header register />
      <div className={style.wrapper}>
        <h1 className={style.title}>{t('formForgotPassword.title')}</h1>
        {renderContent()}
      </div>
    </section>
  );
};

export default RestorePassword;
